import { useCallback, useState, type JSX } from 'react';
import { Icon } from '../components/Icon';
import { ViewCanvas } from '../components/ViewCanvas';
import { useEditedSceneId, useProject, useRuntime, useUi, useUiContext } from '../context';
import { TransitionBar } from '../panels/transitions/TransitionBar';
import { Editor, type ZoomMode } from './Editor';
import { SourceToolbar } from './SourceToolbar';
import './preview.css';

function SceneTitle({ label, sceneId, tone }: { label: string; sceneId: string | null; tone: 'program' | 'preview' }): JSX.Element {
  const name = useProject((p) => (sceneId ? p.scenes[sceneId]?.name : undefined));
  return (
    <div className="preview-pane-title">
      <span className={`ui-badge ${tone}`}>{label}</span>
      <span className="ui-ellipsis">{name ?? '—'}</span>
    </div>
  );
}

/** Central dock: the editable picture, or preview and program side by side in studio mode. */
export function PreviewPanel(): JSX.Element {
  const { ui } = useUiContext();
  const studioMode = useRuntime((r) => r.studioMode);
  const programSceneId = useRuntime((r) => r.programSceneId);
  const editedSceneId = useEditedSceneId();
  const snapEnabled = useUi((s) => s.prefs.snapEnabled);
  const [zoom, setZoom] = useState<ZoomMode>({ type: 'fit' });
  const [scale, setScale] = useState(1);
  const onScale = useCallback((value: number) => setScale(value), []);

  return (
    <div className="preview-panel">
      <div className="preview-stage">
        {editedSceneId ? (
          <div className="preview-pane">
            {studioMode ? <SceneTitle label="Preview" sceneId={editedSceneId} tone="preview" /> : null}
            <Editor
              sceneId={editedSceneId}
              target={studioMode ? { type: 'preview' } : { type: 'program' }}
              zoom={zoom}
              onZoomChange={setZoom}
              onScale={onScale}
            />
          </div>
        ) : (
          <div className="ui-empty ui-grow">No scene is active.</div>
        )}
        {studioMode ? (
          <>
            <div className="preview-middle">
              <TransitionBar />
            </div>
            <div className="preview-pane">
              <SceneTitle label="Program" sceneId={programSceneId} tone="program" />
              <ViewCanvas target={{ type: 'program' }} label="Program picture" className="preview-program" />
            </div>
          </>
        ) : null}
      </div>
      <SourceToolbar />
      <div className="preview-toolbar" role="toolbar" aria-label="Preview view">
        <button type="button" className={zoom.type === 'fit' ? 'ui-btn active' : 'ui-btn'} onClick={() => setZoom({ type: 'fit' })}>
          Fit
        </button>
        <button type="button" className={zoom.type === 'actual' ? 'ui-btn active' : 'ui-btn'} onClick={() => setZoom({ type: 'actual' })}>
          100%
        </button>
        <span className="ui-mono ui-dim" aria-label="Zoom level">
          {Math.round(scale * 100)}%
        </span>
        <span className="ui-grow" />
        <button
          type="button"
          className={snapEnabled ? 'ui-btn active' : 'ui-btn'}
          aria-pressed={snapEnabled}
          title="Snap items to the canvas and to each other (hold Ctrl to suspend)"
          onClick={() => ui.getState().setPrefs({ snapEnabled: !snapEnabled })}
        >
          <Icon name="link" size={14} /> Snapping
        </button>
        <span className="ui-dim preview-toolbar-hint">Wheel to zoom · Space-drag to pan · Alt-drag a handle to crop</span>
        <span className="preview-toolbar-help" tabIndex={0} role="img"
          aria-label="Preview shortcuts: wheel to zoom, Space-drag to pan, Alt-drag a handle to crop"
          title="Wheel to zoom · Space-drag to pan · Alt-drag a handle to crop">?</span>
      </div>
    </div>
  );
}
